import { useEffect, useState, type ReactNode } from "react";
import { Sidebar } from "./Sidebar";
import { Icon } from "./Icon";

const COLLAPSED_KEY = "recspec.sidebar.collapsed";
const COLLAPSED_WIDTH = 56;
const EXPANDED_WIDTH = 232;
/** Below this viewport width the sidebar becomes an off-canvas drawer
 *  (triggered by `.recspec-mobile-trigger` per mobile.css). The desktop
 *  `collapsed` toggle remains for explicit user preference above the
 *  breakpoint. */
const MOBILE_BREAKPOINT = 720;

/** Persistent app chrome: sidebar on the left, main content on the right.
 *  At viewports > 720 px the sidebar is inline and supports an icon-only
 *  collapsed mode (user choice persisted in localStorage). At viewports
 *  ≤ 720 px (per mobile.css) the sidebar becomes an off-canvas drawer
 *  driven by `navOpen`, with a hamburger trigger inside `<main>` and a
 *  dismissal scrim. See add-production-shell-screens §10 / design D9.
 */
export function AppShell({
  sidebarFooter,
  sidebarConfigCard,
  children,
}: {
  /** Footer content for the sidebar (e.g. user identity + sign-out). */
  sidebarFooter?: ReactNode;
  /** Bottom config card for the sidebar (e.g. current meeting summary). */
  sidebarConfigCard?: ReactNode;
  children: ReactNode;
}) {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    const stored = window.localStorage.getItem(COLLAPSED_KEY);
    if (stored === "true") return true;
    if (stored === "false") return false;
    // First visit: collapse on small viewports (above the drawer breakpoint).
    return window.innerWidth < 900;
  });

  /** Whether the viewport is in mobile-drawer mode (≤ 720 px). At this
   *  breakpoint mobile.css renders the sidebar as an off-canvas drawer
   *  with plenty of width; the desktop icon-only collapsed look would
   *  hide every nav LABEL inside the drawer (icons render, text doesn't
   *  — `Sidebar` gates the `<div>{item.label}</div>` block on
   *  `!collapsed`). Force the sidebar to expanded mode in the drawer so
   *  iPhone users see the labels next to the icons. */
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= MOBILE_BREAKPOINT;
  });

  /** Drawer-open state. Only meaningful at viewports ≤ 720 px (CSS-gated). */
  const [navOpen, setNavOpen] = useState<boolean>(false);

  // Persist user choice, but only after they've toggled (the first auto-
  // collapse for a small viewport shouldn't lock them in if they later
  // resize up).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = window.localStorage.getItem(COLLAPSED_KEY);
    if (stored !== null) {
      window.localStorage.setItem(COLLAPSED_KEY, String(collapsed));
    }
  }, [collapsed]);

  // Auto-close the drawer if the viewport grows past the breakpoint — the
  // drawer-mode is no longer in effect and leaving navOpen=true would
  // restore it on the next resize down without a tap. Also keeps the
  // `isMobile` flag in sync with viewport changes (iPhone rotation,
  // iPad split-view, browser-resize on desktop) so the
  // `effectiveCollapsed` derivation below picks the right mode.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => {
      const mobile = window.innerWidth <= MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      if (!mobile && navOpen) setNavOpen(false);
    };
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [navOpen]);

  const toggle = () => {
    setCollapsed((c) => {
      const next = !c;
      if (typeof window !== "undefined") {
        window.localStorage.setItem(COLLAPSED_KEY, String(next));
      }
      return next;
    });
  };

  const closeNav = () => setNavOpen(false);

  // Desktop respects the user's collapse preference; mobile drawer
  // forces expanded so labels render alongside icons inside the drawer.
  // mobile.css already overrides `grid-template-columns` to 1fr at this
  // breakpoint, but aligning the React state to `false` keeps the
  // Sidebar's internal rendering correct.
  const effectiveCollapsed = isMobile ? false : collapsed;

  return (
    <div
      className={"recspec-shell" + (navOpen ? " nav-open" : "")}
      style={{
        display: "grid",
        gridTemplateColumns: `${effectiveCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH}px 1fr`,
        height: "100vh",
        overflow: "hidden",
        transition: "grid-template-columns .15s ease-out",
      }}
    >
      <Sidebar
        footer={sidebarFooter}
        configCard={sidebarConfigCard}
        collapsed={effectiveCollapsed}
        onToggle={toggle}
        onClose={closeNav}
      />
      {navOpen && (
        <div
          className="recspec-nav-scrim"
          onClick={closeNav}
          aria-hidden="true"
        />
      )}
      <main
        style={{
          overflow: "auto",
          background: "var(--paper)",
          position: "relative",
        }}
      >
        <button
          type="button"
          className="recspec-mobile-trigger btn sm"
          onClick={() => setNavOpen(true)}
          aria-label="Open menu"
        >
          <Icon name="list" size={14} />
        </button>
        {children}
      </main>
    </div>
  );
}
